/**
 * Analytics Provider React Context
 */
import React, { createContext, useEffect, useState, useCallback } from 'react';
import { AnalyticsAdapter, AnalyticsEvent, AnalyticsUser } from './types';
import { useBrands } from '@/lib/BrandsProvider';

// Define event names directly in this file to avoid importing from events.ts
// which could be blocked by privacy tools
export const EVENTS = {
  // User lifecycle events
  USER_SIGNED_UP: 'user_signed_up',
  USER_LOGGED_IN: 'user_logged_in',
  USER_LOGGED_OUT: 'user_logged_out',
  USER_ONBOARDING_STARTED: 'user_onboarding_started',
  USER_ONBOARDING_COMPLETED: 'user_onboarding_completed',
  
  // Brand events
  BRAND_CREATED: 'brand_created',
  BRAND_EDITED: 'brand_edited',
  BRAND_SELECTED: 'brand_selected',
  
  // Campaign events
  CAMPAIGN_CREATED: 'campaign_created',
  CAMPAIGN_EDITED: 'campaign_edited',
  CAMPAIGN_DELETED: 'campaign_deleted',
  CAMPAIGN_VIEWED: 'campaign_viewed',
  
  // Content events
  CONTENT_TYPE_SELECTED: 'content_type_selected',
  CONTENT_GENERATION_STARTED: 'content_generation_started',
  CONTENT_GENERATION_COMPLETED: 'content_generation_completed',
  CONTENT_GENERATION_ERROR: 'content_generation_error',
  CONTENT_EDITED: 'content_edited',
  CONTENT_PUBLISHED: 'content_published',
  CONTENT_SHARED: 'content_shared',
  
  // Convergence events
  CONVERGENCE_VIEWED: 'convergence_viewed',
  CONVERGENCE_FILTERED: 'convergence_filtered',
  CONVERGENCE_RERANKED: 'convergence_reranked',
  CONVERGENCE_HIGHLIGHTED: 'convergence_highlighted',
  
  // Feature usage events
  FEATURE_USED: 'feature_used',
  AI_ASSIST_REQUESTED: 'ai_assist_requested',
  TOOL_EXECUTED: 'tool_executed',
  SITE_SCRAPED: 'site_scraped',
  
  // UI interaction events
  PAGE_VIEWED: 'page_viewed',
  BUTTON_CLICKED: 'button_clicked',
  FORM_SUBMITTED: 'form_submitted',
  MODAL_OPENED: 'modal_opened',
  
  // Subscription events
  SUBSCRIPTION_VIEWED: 'subscription_viewed',
  SUBSCRIPTION_CHANGED: 'subscription_changed',
  SUBSCRIPTION_CANCELLED: 'subscription_cancelled',
  CHECKOUT_STARTED: 'checkout_started',
  CHECKOUT_COMPLETED: 'checkout_completed',
  
  // Error events
  ERROR_OCCURRED: 'error_occurred'
} as const;

// TypeScript type for event names
export type EventName = typeof EVENTS[keyof typeof EVENTS];

interface AnalyticsContextValue {
  track: (event: AnalyticsEvent) => void;
  trackEvent: (eventName: EventName, properties?: Record<string, any>) => void;
  identify: (user: AnalyticsUser) => void;
  page: (name: string, properties?: Record<string, any>) => void;
  reset: () => void;
  enabled: boolean;
  setEnabled: (enabled: boolean) => void;
  events: typeof EVENTS;
}

export const AnalyticsContext = createContext<AnalyticsContextValue | undefined>(undefined);

interface AnalyticsProviderProps {
  adapter: AnalyticsAdapter;
  options: any;
  children: React.ReactNode;
  disabled?: boolean;
}

/**
 * Custom error boundary for analytics to catch any issues with analytics
 * and prevent them from breaking the application
 */
class AnalyticsErrorBoundary extends React.Component<{children: React.ReactNode}, {hasError: boolean}> {
  constructor(props: {children: React.ReactNode}) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    console.debug('Analytics error caught by boundary:', error.message);
  }

  render() {
    if (this.state.hasError) {
      // Just render children without analytics
      return this.props.children;
    }
    return this.props.children;
  }
}

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({
  adapter,
  options,
  children,
  disabled = false,
}) => {
  const [enabled, setEnabled] = useState(!disabled);
  const brandsContext = useBrands();
  const selectedBrand = brandsContext?.selectedBrand;

  useEffect(() => {
    try {
      adapter.initialize(options);
      
      if (disabled) {
        adapter.disable();
        setEnabled(false);
      }
      
      return () => {
        adapter.reset();
      };
    } catch (error) {
      console.debug('Analytics initialization error:', error);
      setEnabled(false);
    }
  }, [adapter, options, disabled]);

  useEffect(() => {
    try {
      if (enabled) {
        adapter.enable();
      } else {
        adapter.disable();
      }
    } catch (error) {
      console.debug('Analytics toggle error:', error);
    }
  }, [adapter, enabled]);

  const identify = useCallback((user: AnalyticsUser) => {
    try {
      if (enabled) {
        adapter.identify(user);
      }
    } catch (error) {
      console.debug('Analytics identify error:', error);
    }
  }, [adapter, enabled]);

  const track = useCallback((event: AnalyticsEvent) => {
    try {
      if (enabled) {
        // Enrich event with common properties
        const enrichedProperties = {
          ...event.properties,
          environment: import.meta.env.MODE, // 'development' or 'production'
          timestamp: new Date().toISOString(),
          brand_id: selectedBrand?.id,
          url: window.location.href,
          path: window.location.pathname,
        };
        
        adapter.track({
          name: event.name,
          properties: enrichedProperties,
        });
      }
    } catch (error) {
      console.debug('Analytics track error:', error);
    }
  }, [adapter, enabled, selectedBrand]);

  // Convenience method to track events by name
  const trackEvent = useCallback((eventName: EventName, properties?: Record<string, any>) => {
    try {
      track({ name: eventName, properties });
    } catch (error) {
      console.debug('Analytics trackEvent error:', error);
    }
  }, [track]);

  const page = useCallback((name: string, properties?: Record<string, any>) => {
    try {
      if (enabled) {
        adapter.page(name, properties);
      }
    } catch (error) {
      console.debug('Analytics page error:', error);
    }
  }, [adapter, enabled]);

  const reset = useCallback(() => {
    try {
      adapter.reset();
    } catch (error) {
      console.debug('Analytics reset error:', error);
    }
  }, [adapter]);

  return (
    <AnalyticsErrorBoundary>
      <AnalyticsContext.Provider
        value={{
          track,
          trackEvent,
          identify,
          page,
          reset,
          enabled,
          setEnabled,
          events: EVENTS,
        }}
      >
        {children}
      </AnalyticsContext.Provider>
    </AnalyticsErrorBoundary>
  );
};